import { useMutation } from "@tanstack/react-query";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import {
  useReprocessBulkUpload,
  useRequestBulkDocumentUploadUrls,
} from "@elation/api-client/src/stitch-web";
import type { BulkUploadUrlsRequestFilesItemMimeType } from "@elation/api-client/src/stitch-web/models";

import {
  DocumentDropzone,
  type DocumentDropzoneProps,
} from "@src/features/documents";

import {
  CARE_GAP_DOCUMENT_ACCEPTED_FILE_TYPES,
  CARE_GAP_DOCUMENT_ALLOWED_MIME_TYPES,
  CARE_GAP_DOCUMENT_MAX_FILE_SIZE_BYTES,
} from "../careGapDocumentUpload.constants";

const MAX_FILES_PER_BATCH = 100;

export interface BulkCareGapDocumentUploadResult {
  readonly bulkUploadId: string;
  readonly failedCount?: number;
  readonly jobIds: readonly string[];
  readonly fileCount: number;
}

export interface BulkCareGapDocumentDropzoneProps
  extends Omit<
    DocumentDropzoneProps,
    | "acceptedFileTypes"
    | "ariaLabel"
    | "hint"
    | "isUploading"
    | "label"
    | "onFilesSelected"
  > {
  children?: ReactNode;
  ariaLabel?: string;
  label?: string;
  hint?: string;
  onFilesSelected?: (files: readonly File[]) => void;
  onStatusChange?: (status: string) => void;
  onUploadComplete?: (result: BulkCareGapDocumentUploadResult) => void;
}

function getPendingStatus(files: File[]): string {
  if (files.length === 1) return `Uploading ${files[0].name}...`;
  return `Uploading ${files.length} care gap files...`;
}

function getCompleteStatus(result: BulkCareGapDocumentUploadResult): string {
  if (result.fileCount === 1) return "Extracting care gaps from 1 file.";
  return `Extracting care gaps from ${result.fileCount} files.`;
}

function getPartialStatus(result: BulkCareGapDocumentUploadResult): string {
  const failedCount = result.failedCount ?? 0;
  const extractedLabel =
    result.fileCount === 1 ? "1 file" : `${result.fileCount} files`;
  const failedLabel = failedCount === 1 ? "1 file" : `${failedCount} files`;

  return `Extracting care gaps from ${extractedLabel}; ${failedLabel} failed to upload.`;
}

function getReprocessFailureStatus(fileCount: number, failedCount = 0): string {
  const fileLabel = fileCount === 1 ? "1 file" : `${fileCount} files`;
  if (failedCount > 0) {
    const failedLabel = failedCount === 1 ? "1 file" : `${failedCount} files`;
    return `Uploaded ${fileLabel}, but extraction could not start; ${failedLabel} failed to upload.`;
  }

  return `Uploaded ${fileLabel}, but extraction could not start. Please try again.`;
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Upload failed. Please try again.";
}

export function BulkCareGapDocumentDropzone({
  ariaLabel = "Drop care gap files",
  label = "Drop care gap files",
  hint = "We'll extract care gaps for each patient.",
  multiple = true,
  children,
  onFilesSelected,
  onStatusChange,
  onUploadComplete,
  ...dropzoneProps
}: BulkCareGapDocumentDropzoneProps) {
  const uploadInFlightRef = useRef(false);
  const [isUploadInFlight, setIsUploadInFlight] = useState(false);
  const { mutateAsync: requestUrls, isPending: isRequestingUrls } =
    useRequestBulkDocumentUploadUrls();
  const { mutateAsync: reprocessBulk, isPending: isReprocessing } =
    useReprocessBulkUpload();
  const { mutateAsync: uploadToS3, isPending: isUploadingToS3 } = useMutation({
    mutationFn: async ({ url, file }: { url: string; file: File }) => {
      const response = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`);
      }
    },
  });
  const isUploading =
    isUploadInFlight || isRequestingUrls || isUploadingToS3 || isReprocessing;

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (uploadInFlightRef.current) return;

      onFilesSelected?.(files);
      if (!files.length) return;

      uploadInFlightRef.current = true;
      setIsUploadInFlight(true);

      try {
        if (files.length > MAX_FILES_PER_BATCH) {
          const message = `Too many files: ${files.length} of ${MAX_FILES_PER_BATCH} max per batch.`;
          onStatusChange?.(message);
          toast.error(message);
          return;
        }

        const invalidMime = files.find(
          (file) => !CARE_GAP_DOCUMENT_ALLOWED_MIME_TYPES.has(file.type),
        );
        if (invalidMime) {
          const message = `Unsupported file type: ${invalidMime.name}`;
          onStatusChange?.(message);
          toast.error(message);
          return;
        }

        const oversized = files.find(
          (file) => file.size > CARE_GAP_DOCUMENT_MAX_FILE_SIZE_BYTES,
        );
        if (oversized) {
          const message = `File too large: ${oversized.name} (max 25 MB per file).`;
          onStatusChange?.(message);
          toast.error(message);
          return;
        }

        onStatusChange?.(getPendingStatus(files));

        const { data } = await requestUrls({
          data: {
            files: files.map((file) => ({
              fileName: file.name,
              mimeType: file.type as BulkUploadUrlsRequestFilesItemMimeType,
            })),
          },
        });

        const uploadResults = await Promise.allSettled(
          data.uploads.map(async (upload, index) => {
            const file = files[index];
            if (!file) {
              throw new Error(`Missing file for upload ${upload.fileName}`);
            }

            await uploadToS3({ url: upload.uploadUrl, file });
            return upload;
          }),
        );
        const uploadedDocuments = uploadResults
          .map((result) =>
            result.status === "fulfilled" ? result.value : null,
          )
          .filter((upload) => upload != null);
        const failedUploads = uploadResults.filter(
          (result) => result.status === "rejected",
        );

        failedUploads.forEach((result) => {
          if (result.status === "rejected") {
            console.error("[BulkCareGapDocumentDropzone] S3 upload failed", {
              err: result.reason,
            });
          }
        });

        if (uploadedDocuments.length === 0) {
          throw failedUploads[0]?.status === "rejected"
            ? failedUploads[0].reason
            : new Error("Upload failed. Please try again.");
        }

        const result: BulkCareGapDocumentUploadResult = {
          bulkUploadId: data.bulkUploadId,
          failedCount: failedUploads.length || undefined,
          jobIds: uploadedDocuments.map((upload) => upload.jobId),
          fileCount: uploadedDocuments.length,
        };
        onUploadComplete?.(result);

        try {
          await reprocessBulk({
            bulkUploadId: data.bulkUploadId,
          });
        } catch (err) {
          console.error("[BulkCareGapDocumentDropzone] Reprocess failed", {
            bulkUploadId: data.bulkUploadId,
            err,
          });
          const reprocessStatus = getReprocessFailureStatus(
            result.fileCount,
            failedUploads.length,
          );
          onStatusChange?.(reprocessStatus);
          toast.error(reprocessStatus);
          return;
        }

        const completeStatus =
          failedUploads.length > 0
            ? getPartialStatus(result)
            : getCompleteStatus(result);
        onStatusChange?.(completeStatus);
        if (failedUploads.length > 0) {
          toast.warning(completeStatus);
        } else {
          toast.success(completeStatus);
        }
      } catch (err) {
        const errorStatus = toErrorMessage(err);
        onStatusChange?.(errorStatus);
        toast.error(errorStatus);
      } finally {
        uploadInFlightRef.current = false;
        setIsUploadInFlight(false);
      }
    },
    [
      onFilesSelected,
      onStatusChange,
      onUploadComplete,
      reprocessBulk,
      requestUrls,
      uploadToS3,
    ],
  );

  return (
    <DocumentDropzone
      {...dropzoneProps}
      acceptedFileTypes={CARE_GAP_DOCUMENT_ACCEPTED_FILE_TYPES}
      ariaLabel={ariaLabel}
      hint={hint}
      isUploading={isUploading}
      label={label}
      multiple={multiple}
      onFilesSelected={handleFilesSelected}
    >
      {children}
    </DocumentDropzone>
  );
}
